import { useMemo } from 'react';
import { Check } from 'lucide-react';
import { NestedFullscreenDialog } from '@/components/ui/nested-fullscreen-dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAddressBookEntriesByAddresses } from '@/hooks/organization/use-address-book-entries-by-addresses';
import {
  Section,
  SectionLeftColumn,
  SectionRowComparison,
  SectionComparisonBeforeColumn,
  SectionComparisonAfterColumn,
  SectionTitle,
  SectionDescription,
} from '@/components/shared/SectionComponents';
import type {
  Policy,
  PolicyDestinationAddressEntry,
  PolicyTokenTransferCondition,
} from '@mls-wallet/shared-types';
import { getNetworkDisplayName } from '@mls-wallet/shared-types';
import { toHumanReadableTokenAmount } from '@/utils/tokenAmountUtils';
import { formatName } from '@/lib/name';
import { TokenLogo } from '@/components/shared/token-logo';
import { ContractNameAddress } from '@/components/shared/contract-name-address';
import { AddressDisplay } from '@/components/shared/address-display';
import { formatLocalizedDateTimeWithShortTz } from '@/lib/formatLocalizedDateTimeWithShortTz';

interface PolicyChangeComparisonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  originalPolicy: Policy;
  modifiedPolicy: Policy;
  onSubmit?: () => void;
  onBack?: () => void;
  isSubmitting?: boolean;
}

const ComparisonValue = ({ children }: { children: React.ReactNode }) => {
  const baseClasses =
    'min-w-0 text-sm font-medium min-h-[40px] flex items-center';
  return <div className={baseClasses}>{children}</div>;
};

const AccountDisplay = ({
  account,
}: {
  account: { name: string; address: string };
}) => {
  const formatAddress = (address: string) => {
    return (
      address.substring(0, 6) + '...' + address.substring(address.length - 4)
    );
  };

  return (
    <div className="min-w-0 flex flex-col gap-1">
      <span className="truncate font-medium">{account.name}</span>
      <span className="text-xs text-muted-foreground font-mono">
        {formatAddress(account.address)}
      </span>
    </div>
  );
};

function detectChanges(original: Policy, modified: Policy) {
  const changes = {
    name: original.name !== modified.name,
    description: original.description !== modified.description,
    network: original.networkId !== modified.networkId,
    type: original.type !== modified.type,
    transactionType: original.transactionType !== modified.transactionType,
    accounts: false,
    initiator: false,
    approverSelection: false,
    approverThreshold: false,
    limitation: false,
    tokenId: false,
    amountThreshold: false,
    destinationAddresses: false,
    contractAddresses: false,
    contractFunctions: false,
  };

  const originalAccountIds = new Set(original.accounts.map((a) => a.id));
  const modifiedAccountIds = new Set(modified.accounts.map((a) => a.id));
  changes.accounts =
    original.accounts.length !== modified.accounts.length ||
    original.accounts.some((a) => !modifiedAccountIds.has(a.id)) ||
    modified.accounts.some((a) => !originalAccountIds.has(a.id));

  const origInit = original.initiatorSetting;
  const modInit = modified.initiatorSetting;
  if (!origInit && !modInit) {
    changes.initiator = false;
  } else if (!origInit || !modInit) {
    changes.initiator = true;
  } else if (origInit.type !== modInit.type) {
    changes.initiator = true;
  } else if (origInit.type === 'user' && modInit.type === 'user') {
    changes.initiator = origInit.userId !== modInit.userId;
  } else if (origInit.type === 'group' && modInit.type === 'group') {
    changes.initiator = origInit.groupId !== modInit.groupId;
  }

  const origAppr = original.approverSetting;
  const modAppr = modified.approverSetting;
  if (!origAppr && !modAppr) {
    changes.approverSelection = false;
    changes.approverThreshold = false;
  } else if (!origAppr || !modAppr) {
    changes.approverSelection = true;
    changes.approverThreshold = true;
  } else if (origAppr.type !== modAppr.type) {
    changes.approverSelection = true;
    changes.approverThreshold = false;
  } else if (origAppr.type === 'user' && modAppr.type === 'user') {
    changes.approverSelection = origAppr.userId !== modAppr.userId;
    changes.approverThreshold = false;
  } else if (origAppr.type === 'group' && modAppr.type === 'group') {
    changes.approverSelection = origAppr.groupId !== modAppr.groupId;
    changes.approverThreshold = origAppr.threshold !== modAppr.threshold;
  }

  const origLim = original.limitation;
  const modLim = modified.limitation;
  if (!origLim && !modLim) {
    changes.limitation = false;
  } else if (!origLim || !modLim) {
    changes.limitation = true;
  } else {
    changes.limitation =
      origLim.hours !== modLim.hours ||
      origLim.anchorTimestamp !== modLim.anchorTimestamp ||
      origLim.initiatorScope !== modLim.initiatorScope ||
      origLim.sourceAccountScope !== modLim.sourceAccountScope ||
      origLim.destinationScope !== modLim.destinationScope;
  }

  if (
    original.transactionType === 'TOKEN_TRANSFER' &&
    modified.transactionType === 'TOKEN_TRANSFER' &&
    'tokenTransferCondition' in original &&
    'tokenTransferCondition' in modified
  ) {
    const origCond = original.tokenTransferCondition;
    const modCond = modified.tokenTransferCondition;

    changes.tokenId =
      (origCond?.tokenId ?? null) !== (modCond?.tokenId ?? null);
    changes.amountThreshold =
      (origCond?.amountThreshold ?? null) !==
      (modCond?.amountThreshold ?? null);
    changes.destinationAddresses =
      JSON.stringify(origCond?.destinationAddresses?.sort() ?? []) !==
      JSON.stringify(modCond?.destinationAddresses?.sort() ?? []);
  } else if (changes.transactionType) {
    changes.tokenId = true;
    changes.amountThreshold = true;
    changes.destinationAddresses = true;
  }

  if (
    original.transactionType === 'CONTRACT_INTERACTION' &&
    modified.transactionType === 'CONTRACT_INTERACTION' &&
    'contractInteractionCondition' in original &&
    'contractInteractionCondition' in modified
  ) {
    const origCond = original.contractInteractionCondition;
    const modCond = modified.contractInteractionCondition;

    const isEmptyCondition = (cond: typeof origCond) => {
      if (!cond) return true;
      if (Array.isArray(cond) && cond.length === 0) return true;
      return false;
    };

    const origEmpty = isEmptyCondition(origCond);
    const modEmpty = isEmptyCondition(modCond);

    if (origEmpty && modEmpty) {
      changes.contractAddresses = false;
      changes.contractFunctions = false;
    } else if (origEmpty || modEmpty) {
      changes.contractAddresses = true;
      changes.contractFunctions = true;
    } else if (origCond && modCond) {
      const origAddresses = origCond.map((c) => c.contractAddress).sort();
      const modAddresses = modCond.map((c) => c.contractAddress).sort();
      const origFunctions = [
        ...(origCond[0]?.proxyFunctions?.map((f) => f.name) || []),
        ...(origCond[0]?.implementationFunctions?.map((f) => f.name) || []),
      ].sort();
      const modFunctions = [
        ...(modCond[0]?.proxyFunctions?.map((f) => f.name) || []),
        ...(modCond[0]?.implementationFunctions?.map((f) => f.name) || []),
      ].sort();

      changes.contractAddresses =
        JSON.stringify(origAddresses) !== JSON.stringify(modAddresses);
      changes.contractFunctions =
        JSON.stringify(origFunctions) !== JSON.stringify(modFunctions);
    }
  } else if (changes.transactionType) {
    changes.contractAddresses = true;
    changes.contractFunctions = true;
  }

  return changes;
}

const formatPolicyType = (type: string) => {
  switch (type) {
    case 'AUTO_APPROVAL':
      return 'Auto Approval';
    case 'MANUAL_APPROVAL':
      return 'Manual Approval';
    default:
      return type;
  }
};

const formatTransactionType = (type: string) => {
  switch (type) {
    case 'TOKEN_TRANSFER':
      return 'Token Transfer';
    case 'CONTRACT_INTERACTION':
      return 'Contract Interaction';
    case 'ANY':
      return 'Any';
    default:
      return type;
  }
};

const renderLimitation = (limitation: Policy['limitation']) => {
  if (!limitation) return 'Single transaction';

  const details = [];
  details.push(`${limitation.hours} hours`);
  details.push(
    `Anchor ${formatLocalizedDateTimeWithShortTz(
      limitation.anchorTimestamp * 1000
    )}`
  );
  details.push(
    limitation.initiatorScope === 'PER_ITEM'
      ? 'Per initiator'
      : 'Across all initiators'
  );
  details.push(
    limitation.sourceAccountScope === 'PER_ITEM'
      ? 'Per source account'
      : 'Across all source accounts'
  );
  details.push(
    limitation.destinationScope === 'PER_ITEM'
      ? 'Per destination'
      : 'Across all destinations'
  );

  return (
    <div className="flex flex-col gap-1">
      <span>Time interval</span>
      {details.map((detail, i) => (
        <span key={i} className="text-xs text-muted-foreground">
          {detail}
        </span>
      ))}
    </div>
  );
};

function buildDestinationEntryMap(
  entries: PolicyDestinationAddressEntry[] | undefined
): Map<string, PolicyDestinationAddressEntry> {
  return new Map(
    (entries ?? []).map(
      (entry) => [entry.address.toLowerCase(), entry] as const
    )
  );
}

const renderAddresses = (
  condition: PolicyTokenTransferCondition | null,
  networkId: Policy['networkId']
) => {
  const addresses = condition?.destinationAddresses;
  if (!addresses || addresses.length === 0) return 'Any';

  const destinationEntryByAddress = buildDestinationEntryMap(
    condition?.destinationAddressEntries
  );

  return (
    <div className="flex flex-wrap gap-2">
      {addresses.map((address) => {
        const entry = destinationEntryByAddress.get(address.toLowerCase());

        return (
          <div
            key={address}
            className="min-w-0 max-w-full rounded-full border border-white bg-zinc-100 px-2 py-0.5"
          >
            <AddressDisplay
              address={address}
              name={entry?.name ?? null}
              networkId={networkId}
              showAddressName
              addressNameLayout="inline"
              addressNameClassName="text-xs"
              showCopy
              showBlockExplorerLink
            />
          </div>
        );
      })}
    </div>
  );
};

const renderContractAddresses = (
  networkId: Policy['networkId'],
  addresses: string[] | undefined,
  contractLabelByAddress: Map<string, string | null>
) => {
  if (!addresses || addresses.length === 0) return 'Any';
  return (
    <div className="space-y-2">
      {addresses.map((addr) => (
        <ContractNameAddress
          key={addr}
          address={addr}
          networkId={networkId}
          nameOverride={contractLabelByAddress.get(addr.toLowerCase()) ?? null}
        />
      ))}
    </div>
  );
};

// Exported content component that can be reused
export function PolicyComparisonContent({
  originalPolicy,
  modifiedPolicy,
}: {
  originalPolicy: Policy;
  modifiedPolicy: Policy;
}) {
  const changes = detectChanges(originalPolicy, modifiedPolicy);

  const modifiedAccountIds = new Set(modifiedPolicy.accounts.map((a) => a.id));
  const removedAccounts = originalPolicy.accounts.filter(
    (a) => !modifiedAccountIds.has(a.id)
  );

  const originalTokenCondition =
    originalPolicy.transactionType === 'TOKEN_TRANSFER' &&
    'tokenTransferCondition' in originalPolicy
      ? originalPolicy.tokenTransferCondition
      : null;
  const modifiedTokenCondition =
    modifiedPolicy.transactionType === 'TOKEN_TRANSFER' &&
    'tokenTransferCondition' in modifiedPolicy
      ? modifiedPolicy.tokenTransferCondition
      : null;

  const originalContractCondition =
    originalPolicy.transactionType === 'CONTRACT_INTERACTION' &&
    'contractInteractionCondition' in originalPolicy
      ? originalPolicy.contractInteractionCondition
      : null;
  const modifiedContractCondition =
    modifiedPolicy.transactionType === 'CONTRACT_INTERACTION' &&
    'contractInteractionCondition' in modifiedPolicy
      ? modifiedPolicy.contractInteractionCondition
      : null;

  const referencedContractAddresses = useMemo(() => {
    return Array.from(
      new Set([
        ...(originalContractCondition?.map((entry) => entry.contractAddress) ??
          []),
        ...(modifiedContractCondition?.map((entry) => entry.contractAddress) ??
          []),
      ])
    );
  }, [modifiedContractCondition, originalContractCondition]);

  // Comparison views render only a handful of contracts, so resolve exactly
  // those address-book rows and let those labels override ABI/explorer names.
  const { entryByAddress: contractEntryByAddress } =
    useAddressBookEntriesByAddresses(referencedContractAddresses);

  const contractLabelByAddress = useMemo(() => {
    return new Map(
      referencedContractAddresses.map((address) => [
        address.toLowerCase(),
        contractEntryByAddress.get(address.toLowerCase())?.name ?? null,
      ])
    );
  }, [contractEntryByAddress, referencedContractAddresses]);

  return (
    <>
      {/* Sticky Headers */}
      <div className="sticky top-0 z-40 bg-muted border-b border-border">
        <div className="max-w-5xl mx-auto w-full px-6">
          <div className="grid grid-cols-[1fr_1fr_1fr] gap-0 text-xs text-muted-foreground font-normal uppercase tracking-wide text-center">
            <div className="border-r border-transparent"></div>
            <div className="py-3 border-r border-border">Before</div>
            <div className="py-3">After</div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6 max-w-5xl mx-auto w-full">
        {/* General Section */}
        <Section title="General">
          {/* Network */}
          <SectionRowComparison>
            <SectionLeftColumn>
              <SectionTitle>Blockchain Network</SectionTitle>
              <SectionDescription>
                Which blockchain network this policy applies to
              </SectionDescription>
            </SectionLeftColumn>
            <SectionComparisonBeforeColumn highlight={changes.network}>
              <ComparisonValue>
                {getNetworkDisplayName(originalPolicy.networkId)}
              </ComparisonValue>
            </SectionComparisonBeforeColumn>
            <SectionComparisonAfterColumn highlight={changes.network}>
              <ComparisonValue>
                {getNetworkDisplayName(modifiedPolicy.networkId)}
              </ComparisonValue>
            </SectionComparisonAfterColumn>
          </SectionRowComparison>

          <Separator className="w-full col-span-3" />

          {/* Policy Name */}
          <SectionRowComparison>
            <SectionLeftColumn>
              <SectionTitle>Policy name</SectionTitle>
              <SectionDescription>
                What you want to name this policy
              </SectionDescription>
            </SectionLeftColumn>
            <SectionComparisonBeforeColumn highlight={changes.name}>
              <ComparisonValue>{originalPolicy.name}</ComparisonValue>
            </SectionComparisonBeforeColumn>
            <SectionComparisonAfterColumn highlight={changes.name}>
              <ComparisonValue>{modifiedPolicy.name}</ComparisonValue>
            </SectionComparisonAfterColumn>
          </SectionRowComparison>

          <Separator className="w-full col-span-3" />

          {/* Description */}
          <SectionRowComparison>
            <SectionLeftColumn>
              <SectionTitle>Description</SectionTitle>
              <SectionDescription>
                Optional policy description
              </SectionDescription>
            </SectionLeftColumn>
            <SectionComparisonBeforeColumn highlight={changes.description}>
              <ComparisonValue>
                {originalPolicy.description || '(None)'}
              </ComparisonValue>
            </SectionComparisonBeforeColumn>
            <SectionComparisonAfterColumn highlight={changes.description}>
              <ComparisonValue>
                {modifiedPolicy.description || '(None)'}
              </ComparisonValue>
            </SectionComparisonAfterColumn>
          </SectionRowComparison>
        </Section>

        {/* Policy Type Section */}
        <Section title="Policy type">
          {/* Policy Type */}
          <SectionRowComparison>
            <SectionLeftColumn>
              <SectionTitle>Policy type</SectionTitle>
              <SectionDescription>
                What happens to transactions that match this policy
              </SectionDescription>
            </SectionLeftColumn>
            <SectionComparisonBeforeColumn highlight={changes.type}>
              <ComparisonValue>
                {formatPolicyType(originalPolicy.type)}
              </ComparisonValue>
            </SectionComparisonBeforeColumn>
            <SectionComparisonAfterColumn highlight={changes.type}>
              <ComparisonValue>
                {formatPolicyType(modifiedPolicy.type)}
              </ComparisonValue>
            </SectionComparisonAfterColumn>
          </SectionRowComparison>

          {/* Transaction Approvers (only for manual approval) */}
          {(originalPolicy.type === 'MANUAL_APPROVAL' ||
            modifiedPolicy.type === 'MANUAL_APPROVAL') && (
            <>
              <Separator className="w-full col-span-3" />
              <SectionRowComparison>
                <SectionLeftColumn>
                  <SectionTitle>Transaction approvers</SectionTitle>
                  <SectionDescription>
                    Person or group that should approve transactions
                  </SectionDescription>
                </SectionLeftColumn>
                <SectionComparisonBeforeColumn
                  highlight={changes.approverSelection}
                >
                  <ComparisonValue>
                    {originalPolicy.approverSetting
                      ? originalPolicy.approverSetting.type === 'user'
                        ? formatName(
                            originalPolicy.approverSetting.userName ?? ''
                          )
                        : originalPolicy.approverSetting.groupName
                      : 'No approver selected'}
                  </ComparisonValue>
                </SectionComparisonBeforeColumn>
                <SectionComparisonAfterColumn
                  highlight={changes.approverSelection}
                >
                  <ComparisonValue>
                    {modifiedPolicy.approverSetting
                      ? modifiedPolicy.approverSetting.type === 'user'
                        ? formatName(
                            modifiedPolicy.approverSetting.userName ?? ''
                          )
                        : modifiedPolicy.approverSetting.groupName
                      : 'No approver selected'}
                  </ComparisonValue>
                </SectionComparisonAfterColumn>
              </SectionRowComparison>

              {/* Approval Threshold (only for group approvers) */}
              {(originalPolicy.approverSetting?.type === 'group' ||
                modifiedPolicy.approverSetting?.type === 'group') && (
                <>
                  <Separator className="w-full col-span-3" />
                  <SectionRowComparison>
                    <SectionLeftColumn>
                      <SectionTitle>Approval threshold</SectionTitle>
                      <SectionDescription>
                        Number of approvals required from the group
                      </SectionDescription>
                    </SectionLeftColumn>
                    <SectionComparisonBeforeColumn
                      highlight={changes.approverThreshold}
                    >
                      <ComparisonValue>
                        {originalPolicy.approverSetting?.type === 'group'
                          ? `${originalPolicy.approverSetting.threshold} of ${originalPolicy.approverSetting.members?.length ?? '?'} members`
                          : '-'}
                      </ComparisonValue>
                    </SectionComparisonBeforeColumn>
                    <SectionComparisonAfterColumn
                      highlight={changes.approverThreshold}
                    >
                      <ComparisonValue>
                        {modifiedPolicy.approverSetting?.type === 'group'
                          ? `${modifiedPolicy.approverSetting.threshold} of ${modifiedPolicy.approverSetting.members?.length ?? '?'} members`
                          : '-'}
                      </ComparisonValue>
                    </SectionComparisonAfterColumn>
                  </SectionRowComparison>
                </>
              )}
            </>
          )}
        </Section>

        {/* Transaction Conditions Section */}
        <Section title="Transaction conditions">
          {/* Source Accounts */}
          <SectionRowComparison>
            <SectionLeftColumn>
              <SectionTitle>Transaction source accounts</SectionTitle>
              <SectionDescription>
                Only apply policy to transactions from these accounts
              </SectionDescription>
            </SectionLeftColumn>
            <SectionComparisonBeforeColumn highlight={changes.accounts}>
              <ComparisonValue>
                <div className="space-y-2">
                  {originalPolicy.accounts.length > 0 ? (
                    originalPolicy.accounts.map((account, index) => (
                      <div key={account.id}>
                        <div
                          className={
                            removedAccounts.some((a) => a.id === account.id)
                              ? 'line-through opacity-50'
                              : ''
                          }
                        >
                          <AccountDisplay account={account} />
                        </div>
                        {index < originalPolicy.accounts.length - 1 && (
                          <div className="h-[1px] bg-border my-2"></div>
                        )}
                      </div>
                    ))
                  ) : (
                    <span className="text-muted-foreground">
                      No accounts specified
                    </span>
                  )}
                </div>
              </ComparisonValue>
            </SectionComparisonBeforeColumn>
            <SectionComparisonAfterColumn highlight={changes.accounts}>
              <ComparisonValue>
                <div className="space-y-2">
                  {modifiedPolicy.accounts.length > 0 ? (
                    modifiedPolicy.accounts.map((account, index) => (
                      <div key={account.id}>
                        <AccountDisplay account={account} />
                        {index < modifiedPolicy.accounts.length - 1 && (
                          <div className="h-[1px] bg-border my-2"></div>
                        )}
                      </div>
                    ))
                  ) : (
                    <span className="text-muted-foreground">
                      No accounts specified
                    </span>
                  )}
                </div>
              </ComparisonValue>
            </SectionComparisonAfterColumn>
          </SectionRowComparison>

          <Separator className="w-full col-span-3" />

          {/* Transaction Initiator */}
          <SectionRowComparison>
            <SectionLeftColumn>
              <SectionTitle>Transaction initiator</SectionTitle>
              <SectionDescription>
                Only apply policy to transactions initiated by this person or
                group
              </SectionDescription>
            </SectionLeftColumn>
            <SectionComparisonBeforeColumn highlight={changes.initiator}>
              <ComparisonValue>
                {originalPolicy.initiatorSetting
                  ? originalPolicy.initiatorSetting.type === 'user'
                    ? formatName(originalPolicy.initiatorSetting.userName ?? '')
                    : originalPolicy.initiatorSetting.groupName
                  : 'Any organization member'}
              </ComparisonValue>
            </SectionComparisonBeforeColumn>
            <SectionComparisonAfterColumn highlight={changes.initiator}>
              <ComparisonValue>
                {modifiedPolicy.initiatorSetting
                  ? modifiedPolicy.initiatorSetting.type === 'user'
                    ? formatName(modifiedPolicy.initiatorSetting.userName ?? '')
                    : modifiedPolicy.initiatorSetting.groupName
                  : 'Any organization member'}
              </ComparisonValue>
            </SectionComparisonAfterColumn>
          </SectionRowComparison>

          <Separator className="w-full col-span-3" />

          {/* Transaction Type */}
          <SectionRowComparison>
            <SectionLeftColumn>
              <SectionTitle>Transaction type</SectionTitle>
              <SectionDescription>
                Only apply policy to transactions of this type
              </SectionDescription>
            </SectionLeftColumn>
            <SectionComparisonBeforeColumn highlight={changes.transactionType}>
              <ComparisonValue>
                {formatTransactionType(originalPolicy.transactionType)}
              </ComparisonValue>
            </SectionComparisonBeforeColumn>
            <SectionComparisonAfterColumn highlight={changes.transactionType}>
              <ComparisonValue>
                {formatTransactionType(modifiedPolicy.transactionType)}
              </ComparisonValue>
            </SectionComparisonAfterColumn>
          </SectionRowComparison>

          {/* Token Transfer Conditions */}
          {(originalTokenCondition || modifiedTokenCondition) && (
            <>
              <Separator className="w-full col-span-3" />
              <SectionRowComparison>
                <SectionLeftColumn>
                  <SectionTitle>Token</SectionTitle>
                  <SectionDescription>
                    Only apply policy to transactions sending this token
                  </SectionDescription>
                </SectionLeftColumn>
                <SectionComparisonBeforeColumn highlight={changes.tokenId}>
                  <ComparisonValue>
                    {originalPolicy.transactionType !== 'TOKEN_TRANSFER' ? (
                      <span className="text-muted-foreground">N/A</span>
                    ) : originalTokenCondition?.tokenSymbol ? (
                      <div className="flex items-center gap-2">
                        <TokenLogo
                          symbol={originalTokenCondition.tokenSymbol}
                          logoUrl={
                            originalTokenCondition.tokenLogoUrl ?? undefined
                          }
                          size="sm"
                        />
                        <span>{originalTokenCondition.tokenSymbol}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Any token</span>
                    )}
                  </ComparisonValue>
                </SectionComparisonBeforeColumn>
                <SectionComparisonAfterColumn highlight={changes.tokenId}>
                  <ComparisonValue>
                    {modifiedPolicy.transactionType !== 'TOKEN_TRANSFER' ? (
                      <span className="text-muted-foreground">N/A</span>
                    ) : modifiedTokenCondition?.tokenSymbol ? (
                      <div className="flex items-center gap-2">
                        <TokenLogo
                          symbol={modifiedTokenCondition.tokenSymbol}
                          logoUrl={
                            modifiedTokenCondition.tokenLogoUrl ?? undefined
                          }
                          size="sm"
                        />
                        <span>{modifiedTokenCondition.tokenSymbol}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Any token</span>
                    )}
                  </ComparisonValue>
                </SectionComparisonAfterColumn>
              </SectionRowComparison>

              <Separator className="w-full col-span-3" />
              <SectionRowComparison>
                <SectionLeftColumn>
                  <SectionTitle>Amount threshold</SectionTitle>
                  <SectionDescription>
                    Only apply policy to transactions with this amount or less
                  </SectionDescription>
                </SectionLeftColumn>
                <SectionComparisonBeforeColumn
                  highlight={changes.amountThreshold}
                >
                  <ComparisonValue>
                    {originalPolicy.transactionType !== 'TOKEN_TRANSFER' ? (
                      <span className="text-muted-foreground">N/A</span>
                    ) : originalTokenCondition?.amountThreshold &&
                      originalTokenCondition?.tokenDecimals != null ? (
                      toHumanReadableTokenAmount(
                        originalTokenCondition.amountThreshold,
                        originalTokenCondition.tokenDecimals
                      )
                    ) : (
                      'Any'
                    )}
                  </ComparisonValue>
                </SectionComparisonBeforeColumn>
                <SectionComparisonAfterColumn
                  highlight={changes.amountThreshold}
                >
                  <ComparisonValue>
                    {modifiedPolicy.transactionType !== 'TOKEN_TRANSFER' ? (
                      <span className="text-muted-foreground">N/A</span>
                    ) : modifiedTokenCondition?.amountThreshold &&
                      modifiedTokenCondition?.tokenDecimals != null ? (
                      toHumanReadableTokenAmount(
                        modifiedTokenCondition.amountThreshold,
                        modifiedTokenCondition.tokenDecimals
                      )
                    ) : (
                      'Any'
                    )}
                  </ComparisonValue>
                </SectionComparisonAfterColumn>
              </SectionRowComparison>

              <Separator className="w-full col-span-3" />
              <SectionRowComparison>
                <SectionLeftColumn>
                  <SectionTitle>Destination addresses</SectionTitle>
                  <SectionDescription>
                    Only apply policy to transactions sent to these addresses
                  </SectionDescription>
                </SectionLeftColumn>
                <SectionComparisonBeforeColumn
                  highlight={changes.destinationAddresses}
                >
                  <ComparisonValue>
                    {originalPolicy.transactionType !== 'TOKEN_TRANSFER' ? (
                      <span className="text-muted-foreground">N/A</span>
                    ) : (
                      renderAddresses(
                        originalTokenCondition,
                        originalPolicy.networkId
                      )
                    )}
                  </ComparisonValue>
                </SectionComparisonBeforeColumn>
                <SectionComparisonAfterColumn
                  highlight={changes.destinationAddresses}
                >
                  <ComparisonValue>
                    {modifiedPolicy.transactionType !== 'TOKEN_TRANSFER' ? (
                      <span className="text-muted-foreground">N/A</span>
                    ) : (
                      renderAddresses(
                        modifiedTokenCondition,
                        modifiedPolicy.networkId
                      )
                    )}
                  </ComparisonValue>
                </SectionComparisonAfterColumn>
              </SectionRowComparison>
            </>
          )}

          {/* Contract Interaction Conditions */}
          {(originalContractCondition || modifiedContractCondition) && (
            <>
              <Separator className="w-full col-span-3" />
              <SectionRowComparison>
                <SectionLeftColumn>
                  <SectionTitle>Contract address</SectionTitle>
                  <SectionDescription>
                    Only apply policy to transactions to this contract
                  </SectionDescription>
                </SectionLeftColumn>
                <SectionComparisonBeforeColumn
                  highlight={changes.contractAddresses}
                >
                  <ComparisonValue>
                    {originalPolicy.transactionType !==
                    'CONTRACT_INTERACTION' ? (
                      <span className="text-muted-foreground">N/A</span>
                    ) : originalContractCondition &&
                      originalContractCondition.length > 0 ? (
                      renderContractAddresses(
                        originalPolicy.networkId,
                        originalContractCondition.map((c) => c.contractAddress),
                        contractLabelByAddress
                      )
                    ) : (
                      'Any'
                    )}
                  </ComparisonValue>
                </SectionComparisonBeforeColumn>
                <SectionComparisonAfterColumn
                  highlight={changes.contractAddresses}
                >
                  <ComparisonValue>
                    {modifiedPolicy.transactionType !==
                    'CONTRACT_INTERACTION' ? (
                      <span className="text-muted-foreground">N/A</span>
                    ) : modifiedContractCondition &&
                      modifiedContractCondition.length > 0 ? (
                      renderContractAddresses(
                        modifiedPolicy.networkId,
                        modifiedContractCondition.map((c) => c.contractAddress),
                        contractLabelByAddress
                      )
                    ) : (
                      'Any'
                    )}
                  </ComparisonValue>
                </SectionComparisonAfterColumn>
              </SectionRowComparison>

              <Separator className="w-full col-span-3" />
              <SectionRowComparison>
                <SectionLeftColumn>
                  <SectionTitle>Function names</SectionTitle>
                  <SectionDescription>
                    Only apply policy to transactions calling these functions
                  </SectionDescription>
                </SectionLeftColumn>
                <SectionComparisonBeforeColumn
                  highlight={changes.contractFunctions}
                >
                  <ComparisonValue>
                    {originalPolicy.transactionType !==
                    'CONTRACT_INTERACTION' ? (
                      <span className="text-muted-foreground">N/A</span>
                    ) : (
                      (() => {
                        const proxyFunctions =
                          originalContractCondition?.[0]?.proxyFunctions?.map(
                            (f) => f.name
                          ) || [];
                        const implementationFunctions =
                          originalContractCondition?.[0]?.implementationFunctions?.map(
                            (f) => f.name
                          ) || [];

                        if (
                          proxyFunctions.length === 0 &&
                          implementationFunctions.length === 0
                        ) {
                          return 'Any';
                        }

                        return (
                          <div className="flex flex-col gap-1">
                            {proxyFunctions.map((fn, i) => (
                              <div key={`proxy-${i}`}>
                                {fn}{' '}
                                <span className="text-muted-foreground">
                                  (proxy)
                                </span>
                              </div>
                            ))}
                            {implementationFunctions.map((fn, i) => (
                              <div key={`impl-${i}`}>{fn}</div>
                            ))}
                          </div>
                        );
                      })()
                    )}
                  </ComparisonValue>
                </SectionComparisonBeforeColumn>
                <SectionComparisonAfterColumn
                  highlight={changes.contractFunctions}
                >
                  <ComparisonValue>
                    {modifiedPolicy.transactionType !==
                    'CONTRACT_INTERACTION' ? (
                      <span className="text-muted-foreground">N/A</span>
                    ) : (
                      (() => {
                        const proxyFunctions =
                          modifiedContractCondition?.[0]?.proxyFunctions?.map(
                            (f) => f.name
                          ) || [];
                        const implementationFunctions =
                          modifiedContractCondition?.[0]?.implementationFunctions?.map(
                            (f) => f.name
                          ) || [];

                        if (
                          proxyFunctions.length === 0 &&
                          implementationFunctions.length === 0
                        ) {
                          return 'Any';
                        }

                        return (
                          <div className="flex flex-col gap-1">
                            {proxyFunctions.map((fn, i) => (
                              <div key={`proxy-${i}`}>
                                {fn}{' '}
                                <span className="text-muted-foreground">
                                  (proxy)
                                </span>
                              </div>
                            ))}
                            {implementationFunctions.map((fn, i) => (
                              <div key={`impl-${i}`}>{fn}</div>
                            ))}
                          </div>
                        );
                      })()
                    )}
                  </ComparisonValue>
                </SectionComparisonAfterColumn>
              </SectionRowComparison>
            </>
          )}
        </Section>

        {originalPolicy.transactionType === 'TOKEN_TRANSFER' ||
        modifiedPolicy.transactionType === 'TOKEN_TRANSFER' ? (
          <Section title="Rate limits">
            <SectionRowComparison>
              <SectionLeftColumn>
                <SectionTitle>Rate limits</SectionTitle>
                <SectionDescription>
                  Control how often this policy can be used
                </SectionDescription>
              </SectionLeftColumn>
              <SectionComparisonBeforeColumn highlight={changes.limitation}>
                <ComparisonValue>
                  {renderLimitation(originalPolicy.limitation)}
                </ComparisonValue>
              </SectionComparisonBeforeColumn>
              <SectionComparisonAfterColumn highlight={changes.limitation}>
                <ComparisonValue>
                  {renderLimitation(modifiedPolicy.limitation)}
                </ComparisonValue>
              </SectionComparisonAfterColumn>
            </SectionRowComparison>
          </Section>
        ) : null}
      </div>
    </>
  );
}

export function PolicyChangeComparisonDialog({
  open,
  onOpenChange,
  originalPolicy,
  modifiedPolicy,
  onSubmit,
  onBack,
  isSubmitting = false,
}: PolicyChangeComparisonDialogProps) {
  return (
    <NestedFullscreenDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Review policy changes before submitting"
      noPadding
      bodyContainerClassName="bg-muted"
      footer={
        <div className="flex justify-center gap-2 max-w-xl w-full px-6">
          {onSubmit && onBack ? (
            <>
              <Button
                size="lg"
                variant="outline"
                onClick={onBack}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button size="lg" onClick={onSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  'Submitting...'
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Submit changes
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button
              size="lg"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
          )}
        </div>
      }
    >
      <PolicyComparisonContent
        originalPolicy={originalPolicy}
        modifiedPolicy={modifiedPolicy}
      />
    </NestedFullscreenDialog>
  );
}
